import { ApolloError, useApolloClient, useMutation } from "@apollo/client";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Collapse,
  Input,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import debounce from "debounce-promise";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { gql } from "__generated__/gql";
import { serverGql } from "__generated__/serverGql";
import LoginLayoutButton from "components/Layout/LoginLayoutButton";
import Form from "components/Shared/Forms/Form";
import createFormFieldComponents from "components/Shared/Forms/FormField";
import UrlInput from "components/Shared/Forms/UrlInput";
import useCreateValidSubmitHandler from "components/Shared/Forms/useCreateValidSubmitHandler";
import getMaybeList from "components/Shared/getMaybeList";
import {
  getServerGraphQLErrorMatchersForFields,
  makeServerGraphQLErrorProcessor,
} from "utils/ServerGraphQLErrorExtensions";
import getApolloErrorMessages from "utils/apollo/getApolloErrorMessages";
import serverClient from "utils/apollo/serverClient";
import { assertPresence } from "utils/assertions";

const SIGNUP_BRAND_MUTATION = serverGql(/* ServerGraphQL */ `
  mutation SignupBrand($name: String!, $slug: String!, $website: String!) {
    signupBrand(name: $name, slug: $slug, website: $website) {
      brand {
        id
        name
        slug
        website
        isSubscriptionValid
      }
    }
  }
`);

const BRAND_WEBSITE_AVAILABILITY_QUERY = gql(/* GraphQL */ `
  query SignupBrandWebsiteAvailability($website: String!) {
    brandWebsiteExists(website: $website)
  }
`);

const BRAND_SLUG_AVAILABILITY_QUERY = gql(/* GraphQL */ `
  query SignupBrandSlugAvailability($slug: String!) {
    brandSlugExists(slug: $slug)
  }
`);

type Props = {
  onCompleted: (brandId: string) => Promise<void>;
};

type BrandFormFields = {
  name: string;
  slug: string;
  website: string;
};

const WEBSITE_EXISTS_MESSAGE =
  "An account already exists with this website. Contact support to be added to it.";
const SLUG_EXISTS_MESSAGE =
  "This slug is already in use. Choose a different slug.";

const brandNameSchema = yup.string().trim().required().label("Brand Name");
const brandSlugSchema = yup
  .string()
  .min(3)
  .matches(
    /^[a-z0-9-]+$/,
    // eslint-disable-next-line no-template-curly-in-string
    "${label} must contain only lowercase letters, numbers and hyphens.",
  )
  .required()
  .label("Brand Slug");
const brandWebsiteSchema = yup
  .string()
  .trim()
  .url()
  .required()
  .label("Website");
const brandFieldSchema: yup.ObjectSchema<BrandFormFields> = yup.object().shape({
  name: brandNameSchema,
  slug: brandSlugSchema,
  website: brandWebsiteSchema,
});

const { FormField, ControlledFormField } =
  createFormFieldComponents<BrandFormFields>({
    schema: brandFieldSchema,
  });

const processBrandApolloError = makeServerGraphQLErrorProcessor(
  getServerGraphQLErrorMatchersForFields(["website", "slug"]),
);

function makeBrandSlug(name: string | null | undefined) {
  return (
    name
      ?.trim()
      .toLowerCase()
      .replaceAll(/\s+/g, "-")
      .replaceAll(/[^a-z0-9-]/g, "") ?? ""
  );
}

export default function BrandSignupForm({ onCompleted }: Props) {
  const client = useApolloClient();
  const [brandError, setBrandError] = useState<ApolloError>();

  const validateSlugAvailable = useMemo(
    () =>
      debounce(async (slug: string) => {
        const result = await client.query({
          query: BRAND_SLUG_AVAILABILITY_QUERY,
          variables: { slug },
          fetchPolicy: "network-only",
        });

        return !result.data.brandSlugExists;
      }, 400),
    [client],
  );

  const validateWebsiteAvailable = useMemo(
    () =>
      debounce(async (website: string) => {
        const result = await client.query({
          query: BRAND_WEBSITE_AVAILABILITY_QUERY,
          variables: { website },
          fetchPolicy: "network-only",
        });

        return !result.data.brandWebsiteExists;
      }, 400),
    [client],
  );

  const brandSchema = useMemo(
    (): yup.ObjectSchema<BrandFormFields> =>
      yup.object().shape({
        name: brandNameSchema,
        slug: brandSlugSchema.test(
          "slug-available",
          SLUG_EXISTS_MESSAGE,
          async (slug) =>
            typeof slug !== "string" ||
            !(await brandSlugSchema.isValid(slug)) ||
            validateSlugAvailable(slug),
        ),
        website: brandWebsiteSchema.test(
          "website-available",
          WEBSITE_EXISTS_MESSAGE,
          async (website) =>
            typeof website !== "string" ||
            !(await brandWebsiteSchema.isValid(website)) ||
            validateWebsiteAvailable(website),
        ),
      }),
    [validateSlugAvailable, validateWebsiteAvailable],
  );

  const formContext = useForm<BrandFormFields>({
    mode: "onChange",
    reValidateMode: "onChange",
    resolver: yupResolver(brandSchema),
  });
  const [brandName] = formContext.watch(["name"]);

  const [performSignupBrand, { loading }] = useMutation(SIGNUP_BRAND_MUTATION, {
    client: serverClient,
  });

  useEffect(() => {
    if (!formContext.formState.dirtyFields.slug) {
      formContext.setValue("slug", makeBrandSlug(brandName), {
        shouldValidate: !!formContext.formState.touchedFields.slug,
      });
    }
  }, [formContext, brandName]);

  const [handleSubmit] = useCreateValidSubmitHandler(
    async (formData: BrandFormFields) => {
      setBrandError(undefined);
      const result = await performSignupBrand({ variables: formData });
      const brandId = result.data?.signupBrand?.brand.id;

      assertPresence(
        brandId,
        "Brand signup completed without returning a brand ID.",
      );

      await onCompleted(brandId);
      return result;
    },
    {
      isSuccessToasted: false,
      onNonFieldError: (error) => setBrandError(error),
      processApolloError: processBrandApolloError,
    },
  );

  return (
    <Form formContext={formContext} onValidSubmit={handleSubmit}>
      <Collapse in={!!brandError}>
        <Alert status="error" variant="left-accent" mb={10}>
          <AlertIcon />
          <AlertDescription>
            {brandError && getMaybeList(getApolloErrorMessages(brandError))}
          </AlertDescription>
        </Alert>
      </Collapse>

      <FormField
        name="name"
        label={null}
        placeholder="Brand name"
        autoFocus
        mt={6}
        renderInput={(props) => <Input {...props} size="lg" />}
      />

      <FormField
        name="slug"
        label={null}
        placeholder="brand-name"
        helpText="This is used for things like email addresses and generally shouldn’t be changed later."
        mt={6}
        renderInput={(props) => (
          <Input {...props} size="lg" fontFamily="mono" spellCheck={false} />
        )}
      />

      <ControlledFormField
        name="website"
        label={null}
        mt={6}
        mb={0}
        renderInput={({ field }) => (
          <UrlInput {...field} size="lg" placeholder="brand.com" />
        )}
      />

      <LoginLayoutButton type="submit" mt={6} isLoading={loading}>
        Create Brand
      </LoginLayoutButton>
    </Form>
  );
}
